import { useState, useRef, useEffect, useCallback } from "preact/hooks";
import { lazy, Suspense } from "preact/compat";
import { AnalyticsEvents } from "@murmurations/core";
import emitter from "@webcore/core/events/eventemitter";
import { IS_EYEJACK_APP } from "@webcore/core/utils/utils";
import { openURL } from "@webcore/core/openurl/openurl";
import { Fade } from "@core/components/Transitions/Fade";
import { Spinner } from "@core/components/Elements";
import { AppClipExitScreen } from "@core/components/Layout";
import { useSplashPreloader } from "@core/hooks/useSplashPreloader";
import { useXRSession } from "@core/hooks/useXRSession";
import { useEJXLaunchUrl } from "@core/hooks/useEJXLaunchUrl";
import { buildAppClipUrl, getReturnUrl } from "@core/hooks/useAppClipUrl";

import { Splash } from "./components/Splash";
// AROverlay statically imports the `app` singleton from main.app (Babylon +
// the @webcore WebXR graph). Loading it lazily — it's only rendered in the
// AR_EXPERIENCE state, behind an unmount-on-exit Fade — keeps that whole graph
// out of the microsite's entry chunk so the cover image paints first.
const AROverlay = lazy(() => import("./components/AROverlay").then((m) => ({ default: m.AROverlay })));
/** Warm the AROverlay chunk so it's cached before `modelReady` flips it on —
 * called when an AR launch begins. Shares main.app's already-loaded deps, so
 * this is just the ~10kB overlay module. Resolves the lazy() import instantly. */
const prefetchAROverlay = () => { void import("./components/AROverlay").catch(() => { /* warm-up only; the lazy() + loadMainApp paths own error handling */ }); };
import { LazyBoundary } from "./components/LazyBoundary";
import { PermissionDeniedOverlay } from "./components/PermissionDeniedOverlay";
import { ArErrorScreen, type ArError } from "./components/ArErrorScreen";
import { VideoRecorderUI } from "@core/components/Media";
import { MicrositeShell } from "./microsite/MicrositeShell";
import { MinimalLanding } from "./microsite/MinimalLanding";
import { createLiveStoryQueue, type StoryQueue } from "./data/storyQueue";
import { primeAtVenue } from "./hooks/useAtVenue";
import { trackAnalyticsEvent } from "./analytics";
import { reportError } from "./utils/report";
import { beginLaunch, updateLaunchStage, completeLaunch, reportStaleLaunch, getLaunchStage } from "./utils/launchMarker";
import { detectArBlocker, classifyCameraError, type CameraStatus } from "./utils/arSupport";

import Wordmark from "./assets/MURMURATIONS.svg";
import LoadingBg from "./assets/splash.webp";

/**
 * iOS detection — matches the logic in useXRCheckInline / useUserDevice.
 * Used to route the microsite "Launch in AR" button to the App Clip URL
 * instead of attempting WebXR, which iOS Safari does not support.
 */
function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  return /^iP/.test(navigator.platform) || (/^Mac/.test(navigator.platform) && navigator.maxTouchPoints > 4);
}

/**
 * Append (or replace) a #record hash on a URL while preserving query string.
 * Used by the AR-side Record button to deep-link out to the microsite recording
 * section.
 */
function appendRecordHash(url: string): string {
  try {
    const u = new URL(url);
    u.hash = "record";
    return u.toString();
  } catch {
    const noHash = url.split("#")[0];
    return `${noHash}#record`;
  }
}

// ============================================================================
// APP STATE
// ============================================================================
enum AppState {
  MICROSITE = "MICROSITE",
  SPLASH = "SPLASH",
  TRANSITIONING_XR = "TRANSITIONING_XR",
  AR_EXPERIENCE = "AR_EXPERIENCE",
  APP_CLIP_EXITED = "APP_CLIP_EXITED",
}

/**
 * Catch-all watchdog for the WebXR launch overlay. If we sit on the loading
 * screen this long without reaching a rendering AR state (and without a
 * specific xr-error or load-error already surfacing), show a timeout error so
 * the user is never stranded on the spinner.
 *
 * Deliberately generous: the precise failures (denied prompt, unsupported
 * device, content-load failure) now surface much faster on their own, so this
 * only catches genuinely silent hangs (e.g. a permission prompt left open). It
 * must NOT false-positive on a slow-but-working load — Flock.glb is ~23 MB and
 * downloads during this phase, which can be slow on a congested event network.
 * As a safety net even if it does fire early: the launch keeps running
 * underneath (appState stays TRANSITIONING_XR), and a successful XR start
 * clears this error and proceeds into AR — so a slow load self-heals.
 */
const LAUNCH_TIMEOUT_MS = 60000;

/**
 * Detects a failed dynamic-import (code-split chunk) fetch. The message is
 * browser-specific, so match the common forms across Chrome / Safari / Firefox.
 */
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch/i.test(msg);
}

/** sessionStorage sentinel so the stale-chunk self-heal reloads at most once.
 * Exported so main.tsx's global `vite:preloadError` handler can share the SAME
 * reload budget — the two self-heal paths must agree or they could double-reload. */
export const MAIN_APP_RELOAD_KEY = "mmr_mainapp_reloaded";

let mainAppModule: Promise<typeof import("./main.app")> | null = null;
/**
 * Single code-split loader for main.app (the Babylon/WebXR graph). App.tsx
 * imports it from several effects; a failed fetch is almost always a stale
 * cached index.html pointing at a hashed chunk a redeploy deleted (this app is
 * deployed manually and often — and index.html is now served no-cache, see
 * firebase.json, so this is mainly a transition/transient safety net).
 *
 * We never let the failure become an unhandled rejection (the MURMURATIONS-8
 * Sentry noise): the `.catch` resolves to an EMPTY module so every caller's
 * `app?.x()` optional-chaining is a safe no-op — never a destructure throw.
 *
 * Only the LAUNCH path passes `surfaceErrors`. There, a chunk failure triggers
 * a single full reload to pick up fresh HTML (guarded by the sessionStorage
 * sentinel so a genuinely-broken deploy can't reload-loop); if we've already
 * reloaded this session, it routes to the existing ArErrorScreen via the
 * `load-error` event. Passive warm-up / teardown callers stay silent, so a
 * background failure never throws an AR error screen onto a working microsite.
 */
function loadMainApp(surfaceErrors = false): Promise<Partial<typeof import("./main.app")>> {
  if (!mainAppModule) {
    // Some engines (notably WebKit/iOS Safari) can FULFILL a dynamic import with
    // a nullish/empty module namespace instead of rejecting — e.g. importing the
    // 200-HTML that Firebase's SPA rewrite (** -> /index.html) returns for a
    // deleted hashed chunk, or a WebKit module-eval quirk. Normalise that into a
    // REJECTION so the `.catch` self-heal below handles it uniformly; otherwise
    // the nullish value flows into callers' `({ app }) => …` and throws
    // "Cannot destructure property 'app'…" (Sentry MURMURATIONS-Q/R/S/M, the
    // recurring destructure family). The thrown message is matched by
    // isChunkLoadError() so the launch path still gets its one-time reload.
    mainAppModule = import("./main.app").then((m) => {
      if (!m || typeof m !== "object") {
        throw new Error("Failed to fetch dynamically imported module: main.app resolved to an empty module");
      }
      return m;
    });
  }
  return mainAppModule.catch((err) => {
    // A failed dynamic import is not cached by the browser, so null the handle
    // to allow a later call (e.g. the user tapping Launch again) to re-fetch.
    mainAppModule = null;
    if (!surfaceErrors) return {};

    // Default to "already reloaded" if storage is unreadable (private mode /
    // sandboxed webview) so we never risk a reload loop we can't track.
    let reloadedAlready = true;
    try {
      reloadedAlready = sessionStorage.getItem(MAIN_APP_RELOAD_KEY) === "1";
    } catch {
      reloadedAlready = true;
    }
    if (isChunkLoadError(err) && !reloadedAlready) {
      try {
        sessionStorage.setItem(MAIN_APP_RELOAD_KEY, "1");
      } catch {
        /* ignore — worst case we surface the error below instead of reloading */
      }
      window.location.reload();
      return {};
    }

    // Already self-healed once, or a non-chunk error a reload won't fix:
    // surface the existing recoverable AR error screen instead of looping.
    (emitter as any).emit("load-error", err);
    return {};
  });
}

export function App() {
  const [appState, setAppState] = useState<AppState>(IS_EYEJACK_APP ? AppState.SPLASH : AppState.MICROSITE);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [isPlaced, setIsPlaced] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  /**
   * WebXR launch failure surfaced in the loading overlay (Android/desktop
   * path). null = no error. Set by the xr-error and load-error listeners and
   * the watchdog below; cleared on a fresh launch and on a successful XR start.
   */
  const [arError, setArError] = useState<ArError | null>(null);
  const [storyQueue, setStoryQueue] = useState<StoryQueue | undefined>(undefined);
  /**
   * Gate for the XR auto-start effect below. Set true only after the
   * pre-XR permission round (camera + geolocation) has fully resolved.
   * Without this gate, contentLoaded firing during the await chain would
   * race xrSession.start() against the still-open geolocation prompt and
   * crash the Android Chrome XR tab on first launch (reported 2026-05-13).
   */
  const [permissionsReady, setPermissionsReady] = useState(false);
  /**
   * Set when the flock is loaded and ready to enter AR, but there's no live
   * transient user activation (the cold flock load — or the first-visit camera
   * prompt — outlived the original Launch tap's gesture). requestSession needs
   * transient activation, so auto-starting then fails with SecurityError
   * (MURMURATIONS-C). Instead we surface a "Tap to enter AR" affordance; that
   * fresh tap carries the activation requestSession requires.
   */
  const [needsEnterGesture, setNeedsEnterGesture] = useState(false);
  /**
   * Live flock-download progress (cumulative bytes) for the launch overlay and
   * Splash. Lets a slow ~22MB GLB load show a real progress bar instead of a
   * dead spinner, and feeds the launch watchdog below so a healthy-but-slow
   * download isn't mistaken for a hang (Sentry MURMURATIONS-K). null = idle.
   */
  const [loadProgress, setLoadProgress] = useState<{ loaded: number; total: number } | null>(null);

  const fromLandingRef = useRef(!IS_EYEJACK_APP);
  const safariReturnUrlRef = useRef<string | null>(null);

  const xrSession = useXRSession({
    isLoaded: contentLoaded,
    isInAppClip: IS_EYEJACK_APP,
    autoStartInAppClip: true,
  });

  const { loadingState } = useSplashPreloader({
    minDisplayTime: 2000,
    waitForFonts: true,
  });

  const launchUrl = useEJXLaunchUrl();

  // Report a launch that began on a PRIOR page load but never reached AR and
  // never self-reported — the only telemetry that survives a GPU-process kill
  // or a main-thread block (both of which route around every reportError and
  // the watchdog timer). Runs once, before any beginLaunch this session, so any
  // marker found is necessarily from an earlier load. See utils/launchMarker.ts.
  useEffect(() => {
    reportStaleLaunch();
  }, []);

  // Single source-of-truth fetch for the approved-story queue. The microsite
  // digital twin and the AR scene both consume it — handing the in-flight
  // promise to main.app keeps us from issuing a second /api/stories call.
  useEffect(() => {
    const promise = createLiveStoryQueue();
    promise.then(setStoryQueue);
    loadMainApp().then(({ app }) => {
      app?.setStoryQueue?.(promise);
      // The AR Babylon engine is built lazily (see main.app.ts). The web flow
      // boots it from handleLaunchAR; the iOS App Clip goes straight to AR, so
      // boot it now or the auto-start never gets a `loaded` signal.
      if (IS_EYEJACK_APP) app?.ensureStarted?.();
    });
  }, []);

  useEffect(() => {
    if (!IS_EYEJACK_APP) return;
    const returnUrl = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("returnUrl") : null;
    if (returnUrl && returnUrl.startsWith("http")) {
      safariReturnUrlRef.current = returnUrl;
    } else if (launchUrl && launchUrl.startsWith("http")) {
      safariReturnUrlRef.current = launchUrl;
    }
  }, [launchUrl]);

  const isLoading = loadingState === "loading" || !contentLoaded;

  // Transition to AR_EXPERIENCE when XR starts
  useEffect(() => {
    if (xrSession.isRunning && (appState === AppState.TRANSITIONING_XR || (IS_EYEJACK_APP && appState === AppState.SPLASH))) {
      document.body.classList.add("ar-mode");
      // A real session entered — clear any error the watchdog may have raised
      // while a slow launch was still in flight.
      setArError(null);
      // The launch reached immersive-ar: clear the persisted marker so it isn't
      // mistaken for a silent failure on the next page load.
      updateLaunchStage("in-xr");
      completeLaunch();
      setNeedsEnterGesture(false);
      setAppState(AppState.AR_EXPERIENCE);
    }
  }, [xrSession.isRunning, appState]);

  /**
   * Begin the XR session IF we still hold transient user activation; otherwise
   * surface the "Tap to enter AR" affordance. requestSession('immersive-ar')
   * requires transient activation, which the cold flock load / first-visit
   * camera prompt can outlive — auto-starting then throws SecurityError
   * (MURMURATIONS-C). `navigator.userActivation` lets us tell the two apart so
   * warm/fast launches stay seamless (activation still live) and only the
   * cold/first-visit case asks for a fresh tap. Defaults to starting when the
   * API is unavailable (unchanged behaviour). Not used on the App Clip path.
   */
  const requestXrStart = useCallback(() => {
    if (xrSession.isRunning || xrSession.isStarting) return;
    const ua = typeof navigator !== "undefined"
      ? (navigator as unknown as { userActivation?: { isActive?: boolean } }).userActivation
      : undefined;
    const hasActivation = ua ? Boolean(ua.isActive) : true;
    if (!hasActivation) {
      setNeedsEnterGesture(true);
      return;
    }
    setNeedsEnterGesture(false);
    updateLaunchStage("xr-start-called");
    xrSession.start();
  }, [xrSession]);

  /** "Tap to enter AR" handler — runs inside the user's tap, so transient
   * activation is guaranteed for the requestSession that follows. */
  const handleEnterGestureTap = useCallback(() => {
    setNeedsEnterGesture(false);
    updateLaunchStage("xr-start-called");
    xrSession.start();
  }, [xrSession]);

  // Start XR when content loads while in transitioning state, BUT only after
  // the pre-XR permission round has resolved. Firing xrSession.start while a
  // geolocation prompt is still open on Android Chrome crashes the tab on
  // the "Allow AR" prompt — keep this gated on permissionsReady.
  useEffect(() => {
    if (contentLoaded && permissionsReady && appState === AppState.TRANSITIONING_XR && !xrSession.isRunning && !xrSession.isStarting && !needsEnterGesture) {
      requestXrStart();
    }
  }, [contentLoaded, permissionsReady, appState, xrSession, needsEnterGesture, requestXrStart]);

  useEffect(() => {
    if (xrSession.hasExited && appState === AppState.AR_EXPERIENCE) {
      document.body.classList.remove("ar-mode");

      if (IS_EYEJACK_APP) {
        setAppState(AppState.APP_CLIP_EXITED);
        const urlToOpen = safariReturnUrlRef.current || launchUrl;
        openURL(urlToOpen).catch(() => {});
      } else if (fromLandingRef.current) {
        setAppState(AppState.MICROSITE);
        window.history.pushState({}, "", `/${window.location.search}${window.location.hash}`);
      } else {
        setAppState(AppState.SPLASH);
      }
      setModelReady(false);
      setIsPlaced(false);
      // Dispose the AR Babylon engine so it doesn't keep rendering behind the
      // microsite after exit (the freeze that needed a manual reload), and
      // reset the loaded gate so a re-launch waits for the rebuilt engine's
      // `loaded` signal before starting XR (rather than firing start() against
      // a torn-down session).
      setContentLoaded(false);
      void loadMainApp().then(({ app }) => app?.teardown?.());
    }
  }, [xrSession.hasExited, appState]);

  useEffect(() => {
    const handleLoaded = () => {
      updateLaunchStage("flock-loaded");
      setContentLoaded(true);
    };
    const handleContentLoaded = () => setModelReady(true);
    const handleContentReady = () => setIsPlaced(true);
    const handleRepositioning = () => setIsPlaced(false);

    emitter.on("loaded", handleLoaded);
    emitter.on("content-loaded", handleContentLoaded);
    emitter.on("content-ready", handleContentReady);
    (emitter as any).on("content-repositioning", handleRepositioning);

    loadMainApp().then(({ app }) => {
      if (app?.isReady()) setContentLoaded(true);
    });

    return () => {
      emitter.off("loaded", handleLoaded);
      emitter.off("content-loaded", handleContentLoaded);
      emitter.off("content-ready", handleContentReady);
      (emitter as any).off("content-repositioning", handleRepositioning);
    };
  }, []);

  // Surface WebXR launch failures in the loading overlay instead of hanging on
  // the spinner. These fire from AppBase.startXREvent (xr-error-*) and from
  // MainApp.ensureStarted's load-error catch. The iOS App Clip path has its own
  // guidance via Splash.tsx (useXRErrorGuidance) and no microsite to go "back"
  // to, so we only drive ArErrorScreen on the Android/desktop web flow.
  useEffect(() => {
    if (IS_EYEJACK_APP) return;
    const onPermissionDenied = (e?: { type?: "camera" | "motion" | "mic" }) => {
      const kind = e?.type === "camera" ? "camera" : e?.type === "motion" ? "motion" : "permission";
      setArError({ kind });
    };
    const onNotSupported = () => setArError({ kind: "not-supported" });
    const onUndefined = () => setArError({ kind: "undefined" });
    const onLoadError = (err?: unknown) => {
      const message = err instanceof Error ? err.message : err ? String(err) : undefined;
      setArError({ kind: "load", detail: message?.slice(0, 140) });
    };
    const onCrash = () => setArError({ kind: "crash" });

    emitter.on("xr-error-permission-denied", onPermissionDenied);
    emitter.on("xr-error-not-supported", onNotSupported);
    emitter.on("xr-error-undefined", onUndefined);
    (emitter as any).on("load-error", onLoadError);
    (emitter as any).on("ar-crash", onCrash);

    return () => {
      emitter.off("xr-error-permission-denied", onPermissionDenied);
      emitter.off("xr-error-not-supported", onNotSupported);
      emitter.off("xr-error-undefined", onUndefined);
      (emitter as any).off("load-error", onLoadError);
      (emitter as any).off("ar-crash", onCrash);
    };
  }, []);

  // Live flock-download progress (all platforms). Bucketed to ~0.1MB so it
  // re-renders ~200× at most, not on every byte. Returning the previous object
  // unchanged lets React bail out of the no-op renders.
  useEffect(() => {
    const onProgress = (p?: { loaded: number; total: number }) => {
      if (!p) return;
      setLoadProgress((prev) => {
        const bucket = (n: number) => Math.round(n / 104857.6); // 0.1MB buckets
        if (prev && bucket(prev.loaded) === bucket(p.loaded) && prev.total === p.total) return prev;
        return { loaded: p.loaded, total: p.total };
      });
    };
    (emitter as any).on("load-progress", onProgress);
    return () => (emitter as any).off("load-progress", onProgress);
  }, []);

  // iOS App Clip recovery. The web error effect above bails for IS_EYEJACK_APP,
  // so the App Clip previously had NO surface for a failed flock load — a GLB
  // import timeout left the user stuck on the loading spinner forever (Sentry
  // MURMURATIONS-K). Surface the recoverable load/crash errors here too so the
  // App Clip's ArErrorScreen can offer "Try again". (XR-error-* are WebXR-only,
  // which the App Clip never runs, so they're intentionally not wired here.)
  useEffect(() => {
    if (!IS_EYEJACK_APP) return;
    const onLoadError = (err?: unknown) => {
      const message = err instanceof Error ? err.message : err ? String(err) : undefined;
      setArError((prev) => prev ?? { kind: "load", detail: message?.slice(0, 140) });
    };
    const onCrash = () => setArError((prev) => prev ?? { kind: "crash" });
    (emitter as any).on("load-error", onLoadError);
    (emitter as any).on("ar-crash", onCrash);
    return () => {
      (emitter as any).off("load-error", onLoadError);
      (emitter as any).off("ar-crash", onCrash);
    };
  }, []);

  // Watchdog: if the loading overlay sits too long without reaching a rendering
  // AR state — and nothing more specific has surfaced — show a timeout error so
  // the user can recover. Skipped on the App Clip (Splash owns its errors).
  // Progress-aware: `loadProgress` is in the deps, so every ~0.1MB of flock
  // download restarts the timer — a healthy-but-slow GLB fetch never trips it
  // (only a genuine stall of >LAUNCH_TIMEOUT_MS with no progress does).
  const isLaunchingOverlay =
    appState === AppState.TRANSITIONING_XR || (appState === AppState.AR_EXPERIENCE && !modelReady);
  useEffect(() => {
    // Suppressed while waiting on the user's "Tap to enter AR" gesture — that's
    // a deliberate wait for input, not a silent hang.
    if (IS_EYEJACK_APP || !isLaunchingOverlay || arError || needsEnterGesture) return;
    const id = window.setTimeout(() => {
      // Report BEFORE surfacing the UI error. A watchdog-caught hang is
      // otherwise invisible (the timeout path never threw), and this is also
      // the captured event the otherwise-orphaned load-stage breadcrumbs ride
      // out on. Carries the furthest boot stage reached so a hang stuck at
      // "xr-start-called" (enterXRAsync wedge) is distinguishable from earlier
      // stalls. Only fires while the main thread is alive — a thread block or
      // GPU-process kill is caught instead by reportStaleLaunch on next load.
      reportError(new Error("AR launch watchdog timeout"), {
        where: "watchdog",
        stage: getLaunchStage(),
        appState,
        modelReady,
        timeoutMs: LAUNCH_TIMEOUT_MS,
      });
      setArError((prev) => prev ?? { kind: "timeout" });
    }, LAUNCH_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [isLaunchingOverlay, arError, appState, modelReady, needsEnterGesture, loadProgress]);

  // Any surfaced AR error (xr-error-*, load-error, ar-crash, or the watchdog
  // timeout above) is a terminal launch state the user can see and recover
  // from — clear the persisted launch marker so it isn't re-reported as a
  // silent failure on the next page load. Expected conditions (denied
  // permission, unsupported) are intentionally not Sentry-reported, only
  // cleared; genuine faults were already reported at their throw/timeout site.
  useEffect(() => {
    if (arError) completeLaunch();
  }, [arError]);

  // Hash navigation on MICROSITE. `#record` is a state signal, not a scroll
  // target — MicrositeShell opens the record sheet on it and owns clearing
  // it. Any other hash scrolls to its section then cleans itself up.
  useEffect(() => {
    if (appState !== AppState.MICROSITE) return;
    const hash = window.location.hash;
    if (!hash || hash === "#record") return;
    document.querySelector(hash)?.scrollIntoView({ block: "start", behavior: "smooth" });
    setTimeout(() => {
      const u = new URL(window.location.href);
      u.hash = "";
      window.history.replaceState({}, "", u.pathname + u.search);
    }, 800);
  }, [appState]);

  // ============================================================================
  // AR handlers
  // ============================================================================

  const requestCamera = useCallback(async (): Promise<CameraStatus> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      return "ok";
    } catch (err) {
      // Distinguish denied (permission) from busy (camera held by another app /
      // tab — NotReadableError) from no-camera, so we can show the right
      // recovery instead of a blanket "permission needed".
      return classifyCameraError(err);
    }
  }, []);

  /**
   * Pre-XR launch flow:
   *
   *   tap Launch AR
   *   → show loading screen
   *   → request camera permission (await — blocks XR if denied)
   *   → primeAtVenue() (NO prompt — see hooks/useAtVenue.ts; reads cached
   *                     position only if location was already granted for
   *                     this origin, otherwise resolves to off-site copy)
   *   → xrSession.start() (which itself prompts for "Allow AR")
   *   → placement overlay
   *
   * We deliberately keep this to two native permission sheets (camera +
   * AR). Adding a third sheet (geolocation) in this sequence crashes
   * Android Chrome's XR session start, reproducibly. The geolocation
   * opt-in for on-site coaching lives on the microsite instead — see
   * `requestVenueGeoOptIn` in hooks/useAtVenue.ts.
   */
  const handleLaunchAR = useCallback(async () => {
    fromLandingRef.current = true;
    setPermissionDenied(false);
    setArError(null);
    setPermissionsReady(false);
    setNeedsEnterGesture(false);
    setLoadProgress(null);
    const entryPoint = IS_EYEJACK_APP
      ? "app_clip"
      : new URLSearchParams(window.location.search).get("mode") === "ar"
        ? "minimal_landing"
        : "microsite";
    trackAnalyticsEvent(AnalyticsEvents.AR_LAUNCH, {
      app_surface: "ar",
      ar_entry_point: entryPoint,
    });

    if (IS_EYEJACK_APP) {
      setAppState(AppState.SPLASH);
      return;
    }

    // iOS Safari doesn't support WebXR — redirect to the Apple App Clip,
    // which delivers the same experience natively. Pass the current Safari
    // tab URL as returnUrl so App Clip exit comes back to this microsite.

    if (isIOSSafari()) {
      const returnUrl = getReturnUrl();
      const baseUrl = window.location.origin + window.location.pathname;
      window.location.href = buildAppClipUrl("", { returnUrl, baseUrl });
      return;
    }

    // Browser pre-flight (Android): surface a recovery message BEFORE the
    // spinner if AR can't run in this browser at all — an in-app webview
    // (Instagram/Facebook/…) or a non-WebXR browser (Firefox/Samsung/…). These
    // never enter the launch flow; the ArErrorScreen offers an "Open in Chrome"
    // action. (Missing/stale ARCore is not pre-flighted here — Chrome installs
    // it at session start, and a genuine failure surfaces afterwards with an
    // "install Google Play Services for AR" action.)
    const blocker = detectArBlocker();
    if (blocker) {
      setArError({ kind: blocker });
      return;
    }

    // Loading screen first — user sees the spinner while we ask for camera,
    // then geolocation. Both prompts fire on top of the loading screen, not
    // on top of the WebXR session.
    document.body.classList.add("ar-mode");
    setAppState(AppState.TRANSITIONING_XR);
    setModelReady(false);
    setIsPlaced(false);
    // Stamp the persisted launch marker (carries device/GPU fingerprint). It is
    // advanced at each stage below and cleared on success / surfaced error; if
    // it survives to the next page load the launch died silently.
    beginLaunch("camera-requested", entryPoint);

    // 1. Camera — required. Bail out (with the right recovery) if it won't open.
    const camera = await requestCamera();
    if (camera !== "ok") {
      document.body.classList.remove("ar-mode");
      setAppState(AppState.MICROSITE);
      completeLaunch();
      if (camera === "denied") {
        // Pure permission denial keeps the existing bottom-sheet overlay.
        setPermissionDenied(true);
      } else {
        // Camera busy (in use elsewhere) / no camera — show the specific
        // recovery screen with a "Try again" action.
        setArError({ kind: camera === "busy" ? "camera-busy" : "camera-notfound" });
      }
      return;
    }
    updateLaunchStage("camera-granted");

    // Build the AR Babylon engine + flock now that the user has committed to
    // AR (it's torn down on exit, so this also covers re-launches). Loading
    // runs behind the "Starting AR..." screen; the auto-start effect fires
    // xrSession.start() once `loaded` lands.
    void loadMainApp(true).then(({ app }) => app?.ensureStarted?.());
    prefetchAROverlay();
    updateLaunchStage("ensure-started");

    // 2. Venue check — resolves instantly unless the user has previously
    //    granted geolocation for this origin (in which case getCurrentPosition
    //    returns the cached fix without UI). Never prompts here.
    await primeAtVenue();
    updateLaunchStage("venue-primed");

    // 3. Mark perms ready; the auto-start effect (or the direct call below
    //    when content is already loaded) hands off to xrSession.start, which
    //    triggers the "Allow AR" prompt now that nothing else is in flight.
    setPermissionsReady(true);
    updateLaunchStage("perms-ready");

    if (contentLoaded) {
      requestXrStart();
    }
  }, [xrSession, requestCamera, contentLoaded, requestXrStart]);

  /**
   * App Clip / Splash → BEGIN button. Same serialisation as `handleLaunchAR`
   * but without the camera step (the iOS App Clip frame handles camera
   * permission natively before this code runs).
   */
  const handleSplashClose = useCallback(async () => {
    setArError(null);
    setPermissionsReady(false);
    setLoadProgress(null);
    trackAnalyticsEvent(AnalyticsEvents.AR_LAUNCH, {
      app_surface: "ar",
      ar_entry_point: "app_clip",
    });

    // Ensure the AR engine is built (idempotent — App Clip already boots it on
    // mount; this covers any other path that reaches the splash).
    void loadMainApp(true).then(({ app }) => app?.ensureStarted?.());
    prefetchAROverlay();

    document.body.classList.add("ar-mode");
    setAppState(AppState.TRANSITIONING_XR);
    setModelReady(false);
    setIsPlaced(false);
    beginLaunch("app-clip-begin", "app_clip");
    updateLaunchStage("ensure-started");

    // Venue check resolves immediately unless geolocation has been granted
    // previously (see comments on `primeAtVenue`). No native prompt fires
    // here, by design.
    await primeAtVenue();
    setPermissionsReady(true);
    updateLaunchStage("perms-ready");

    if (contentLoaded) {
      updateLaunchStage("xr-start-called");
      xrSession.start();
    }
  }, [xrSession, contentLoaded]);

  const handleExitAR = useCallback(() => {
    trackAnalyticsEvent(AnalyticsEvents.AR_EXIT, {
      app_surface: "ar",
      ar_exit_reason: "close_button",
    });
    xrSession.stop();
  }, [xrSession]);

  // Record button inside AROverlay. On Android/desktop WebXR we end the XR
  // session and queue a hash navigation so the microsite scrolls to #record
  // when MICROSITE re-mounts. On iOS App Clip there is no WebXR session to
  // end — we deep-link out to the Safari microsite URL with #record appended.
  const handleArRecord = useCallback(() => {
    trackAnalyticsEvent(AnalyticsEvents.AR_EXIT, {
      app_surface: "ar",
      ar_exit_reason: "record_story",
    });
    if (IS_EYEJACK_APP) {
      const target = safariReturnUrlRef.current || launchUrl;
      if (target) {
        const recordUrl = appendRecordHash(target);
        openURL(recordUrl).catch(() => {});
      }
      return;
    }
    try {
      const u = new URL(window.location.href);
      u.hash = "#record";
      window.history.replaceState({}, "", u.pathname + u.search + u.hash);
    } catch {
      window.location.hash = "#record";
    }
    xrSession.stop();
  }, [xrSession, launchUrl]);

  const handleBackToLanding = useCallback(() => {
    document.body.classList.remove("ar-mode");
    setAppState(AppState.MICROSITE);
    window.history.pushState({}, "", `/${window.location.search}${window.location.hash}`);
  }, []);

  // Recovery from a failed AR launch (ArErrorScreen "Back to site"). Mirrors the
  // XR-exit cleanup: drop ar-mode, return to the microsite, reset the launch
  // gates, and tear down the half-built engine so the next Launch AR rebuilds
  // from scratch (rather than re-using a partially-initialised session).
  const handleArErrorBack = useCallback(() => {
    trackAnalyticsEvent(AnalyticsEvents.AR_EXIT, {
      app_surface: "ar",
      ar_exit_reason: "error_back",
    });
    setArError(null);
    document.body.classList.remove("ar-mode");
    setAppState(AppState.MICROSITE);
    setModelReady(false);
    setIsPlaced(false);
    setPermissionsReady(false);
    setContentLoaded(false);
    setNeedsEnterGesture(false);
    void loadMainApp().then(({ app }) => app?.teardown?.());
    window.history.pushState({}, "", `/${window.location.search}${window.location.hash}`);
  }, []);

  // "Try again" from the error screen: tear down any half-built engine and
  // reset the launch gates, then re-run the launch flow from the top. Used for
  // recoverable failures (camera busy, content-load, timeout, session-start
  // failure) where the user can fix the cause (close the other app, reconnect,
  // update ARCore) and immediately retry without bouncing back to the microsite.
  // App Clip "Back to site": there is no in-frame microsite to return to, so
  // deep-link out to the Safari microsite if we have a return URL (else the
  // error screen omits Back and offers only "Try again").
  const handleAppClipBack = useCallback(() => {
    const target = safariReturnUrlRef.current || launchUrl;
    if (target) openURL(target).catch(() => {});
  }, [launchUrl]);

  const handleArErrorRetry = useCallback(() => {
    // handleLaunchAR re-emits the AR_LAUNCH analytics event itself.
    setArError(null);
    setModelReady(false);
    setIsPlaced(false);
    setPermissionsReady(false);
    setContentLoaded(false);
    setNeedsEnterGesture(false);
    setLoadProgress(null);
    if (IS_EYEJACK_APP) {
      // The App Clip has no microsite launch flow to re-run (handleLaunchAR just
      // returns to Splash on iOS). Tear the half-built engine down — which
      // resets ensureStarted's idempotency guard — and rebuild it, re-running
      // loadAsync. Back on Splash so the auto-start flow re-engages on success.
      setAppState(AppState.SPLASH);
      void loadMainApp(true).then(({ app }) => {
        app?.teardown?.();
        app?.ensureStarted?.();
      });
      return;
    }
    void loadMainApp()
      .then(({ app }) => app?.teardown?.())
      .finally(() => { void handleLaunchAR(); });
  }, [handleLaunchAR]);

  const showMicrosite = appState === AppState.MICROSITE;
  // `?mode=ar` swaps the full microsite for a stripped landing page — title
  // and Launch AR button only. Intended for QR-code / direct-link entry
  // where the digital twin would just be visual noise on the way to AR. The
  // story queue still fetches at App level so AR has its data when launched.
  const isMinimalArMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("mode") === "ar";

  // Launch-overlay/Splash download readout. Show MB always once bytes arrive;
  // show a determinate % + bar only when the total is known AND sane (with
  // Brotli the reported total can be the compressed size, so guard loaded<=total
  // to avoid a misleading >100%). null = no readout yet.
  const dlMB = loadProgress && loadProgress.loaded > 0 ? (loadProgress.loaded / 1048576).toFixed(1) : null;
  const dlFraction =
    loadProgress && loadProgress.total > 0 && loadProgress.loaded <= loadProgress.total
      ? loadProgress.loaded / loadProgress.total
      : null;
  const dlLabel = dlMB ? (dlFraction !== null ? `${Math.round(dlFraction * 100)}%` : `${dlMB} MB`) : null;

  return (
    <>
      {showMicrosite && (isMinimalArMode ? <MinimalLanding onLaunchAR={handleLaunchAR} /> : <MicrositeShell queue={storyQueue} onLaunchAR={handleLaunchAR} />)}

      <PermissionDeniedOverlay
        visible={permissionDenied}
        errorMessage={"Camera permission is required for AR<br /><br />Please allow camera access in your browser settings and try again."}
        onClose={() => setPermissionDenied(false)}
      />

      {/* Splash — WebXR onboarding (iOS App Clip or Android flow) */}
      <Fade visible={appState === AppState.SPLASH}>
        <Splash
          isLoading={isLoading}
          onClose={handleSplashClose}
          onBack={fromLandingRef.current ? handleBackToLanding : undefined}
          autoStart={IS_EYEJACK_APP}
          progressLabel={dlLabel}
          progressFraction={dlFraction}
        />
      </Fade>

      {/* Loading overlay — brand treatment over the City Hall photo while
          the XR session spins up (no design frame exists; inferred from the
          splash language). */}
      <Fade visible={(appState === AppState.TRANSITIONING_XR || (appState === AppState.AR_EXPERIENCE && !modelReady)) && !arError} duration={300}>
        <div
          className="fixed inset-0 flex flex-col items-center justify-center pointer-events-auto z-50"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(21,11,42,0.25) 0%, rgba(21,11,42,0.65) 80%, rgba(21,11,42,0.9) 100%), url(${LoadingBg})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            minHeight: "100dvh",
            color: "var(--brand-text)",
          }}
        >
          <img src={Wordmark} alt="Murmurations" className="w-full max-w-[300px] mb-12" style={{ filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.35))" }} />
          {needsEnterGesture && appState === AppState.TRANSITIONING_XR ? (
            <button
              type="button"
              onClick={handleEnterGestureTap}
              className="mt-2 px-8 py-3 rounded-full bg-white text-[var(--brand-bg-deep,#150b2a)] text-base font-semibold transition-colors"
            >
              Tap to enter AR
            </button>
          ) : (
            <>
              <Spinner size="2md" variant="light" />
              <p className="text-white/75 text-sm mt-4">{appState === AppState.AR_EXPERIENCE ? "Loading..." : "Starting AR..."}</p>
              {dlLabel && (
                <div className="mt-3 w-48 max-w-[60vw] flex flex-col items-center gap-1.5">
                  {dlFraction !== null && (
                    <div className="h-1 w-full rounded-full bg-white/20 overflow-hidden">
                      <div className="h-full bg-white/80" style={{ width: `${Math.round(dlFraction * 100)}%`, transition: "width 200ms linear" }} />
                    </div>
                  )}
                  <p className="text-white/45 text-xs">{dlLabel}</p>
                </div>
              )}
            </>
          )}
        </div>
      </Fade>

      {/* AR launch failure — replaces the loading overlay with a plain-English
          reason + recovery. Now shown on the iOS App Clip too (previously gated
          off, leaving an App Clip GLB-load timeout stuck on the spinner forever
          — Sentry MURMURATIONS-K). On the App Clip, Back deep-links out to the
          Safari microsite when a return URL exists, else only "Try again" shows. */}
      {arError && (
        <ArErrorScreen
          error={arError}
          onBack={
            IS_EYEJACK_APP
              ? (safariReturnUrlRef.current || launchUrl ? handleAppClipBack : undefined)
              : handleArErrorBack
          }
          onRetry={handleArErrorRetry}
        />
      )}

      {/* AR overlay — shown when model is ready */}
      <Fade visible={appState === AppState.AR_EXPERIENCE && modelReady} duration={300}>
        <LazyBoundary
          label="ar-overlay"
          fallback={
            <div className="fixed inset-0 pointer-events-auto z-50 flex items-end justify-center pb-8">
              <button
                type="button"
                onClick={handleExitAR}
                className="px-5 py-3 rounded-full bg-black/60 text-white text-sm backdrop-blur"
              >
                Exit AR
              </button>
            </div>
          }
        >
          <Suspense fallback={null}>
            <AROverlay onExit={handleExitAR} onRecord={handleArRecord} isRecording={isRecording} />
          </Suspense>
        </LazyBoundary>
      </Fade>

      {/* Video recorder — only after placement confirmed */}
      {/* TODO: re-enable once the record path is fixed. Pressing record hard-
          crashes the Android Chrome XR tab. Suspected cause: MediaRecorderWeb
          enables WebXRRawCameraAccess + sets up RTTs + post-process inside an
          active immersive-ar session, and one of those steps (likely the
          camera-access enable or the EXTERNAL_OES texture binding in the
          composite shader) throws inside the XR rAF callback. We've already
          deferred the camera-access enable to record-press time (see
          main.app.ts recordStartAsync), so the crash is now isolated to the
          recording pipeline itself. Disabling the UI surface for now so users
          can't trigger it; revisit before July 4 launch. */}
      {/*
      <Fade visible={appState === AppState.AR_EXPERIENCE && modelReady && isPlaced} duration={300}>
        <VideoRecorderUI
          buttonPosition="bottom-center"
          shrink
          shareTitle="Murmurations"
          onRecordingStart={() => setIsRecording(true)}
          onRecordingStop={() => setIsRecording(false)}
        />
      </Fade>
      */}

      {appState === AppState.APP_CLIP_EXITED && <AppClipExitScreen />}
    </>
  );
}
